import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, minorToInput } from '@/lib/officialsApi';
import { Field } from './PayBits';

const snapshot = (eid, rates, budgets) => ({
  vals: Object.fromEntries(rates.filter(r => r.scope === 'event' && r.ref === eid).map(r => [r.role_key, minorToInput(r.amount_minor)])),
  budget: minorToInput(budgets[eid]),
});

// target: { id, ... } to edit, or { event_id? } to create (optionally prefilled from a league).
export const TemplateDialog = ({ target, orgId, roles, events, rates, budgets, currency, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [vals, setVals] = useState({});
  const [budget, setBudget] = useState('');
  const [from, setFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const editing = !!target?.id;
  useEffect(() => {
    if (!target) return;
    if (target.id) {
      setName(target.name);
      setVals(Object.fromEntries(Object.entries(target.rates).map(([k, m]) => [k, minorToInput(m)])));
      setBudget(minorToInput(target.budget_minor));
      setFrom(target.source_event_id || '');
      return;
    }
    const s = target.event_id ? snapshot(target.event_id, rates, budgets) : { vals: {}, budget: '' };
    setName(target.event_id ? `${events.find(e => e.id === target.event_id)?.name || 'League'} fees` : '');
    setVals(s.vals);
    setBudget(s.budget);
    setFrom(target.event_id || '');
  }, [target, rates, budgets, events]);
  const startFrom = (eid) => {
    const s = snapshot(eid, rates, budgets);
    setFrom(eid);
    setVals(s.vals);
    setBudget(s.budget);
    if (!name.trim()) setName(`${events.find(e => e.id === eid)?.name || 'League'} fees`);
  };
  const filled = roles.filter(r => (vals[r.key] ?? '') !== '').length;
  const submit = async () => {
    setBusy(true);
    try {
      const body = { name, rates: vals, budget, source_event_id: from || null };
      const r = await oapi(editing ? `/officials/payments/rate-templates/${target.id}` : '/officials/payments/rate-templates',
        { method: editing ? 'PUT' : 'POST', orgId, body });
      toast.success(editing ? 'Template updated' : `Template "${r.template.name}" saved`);
      onSaved(r.template);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">{editing ? 'Edit template' : 'New rate template'}</DialogTitle>
          <DialogDescription>A named set of fees per role you can apply to any league or tournament. Editing a template never changes leagues it was already applied to.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Template name"><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Youth rates 2026" maxLength={80} data-testid="template-name" /></Field>
          {!editing && (
            <Field label="Start from a league (optional)">
              <Select value={from || undefined} onValueChange={startFrom}>
                <SelectTrigger data-testid="template-start-from"><SelectValue placeholder="Blank - enter fees below" /></SelectTrigger>
                <SelectContent>{events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <div className="space-y-2 rounded-xl border p-3">
            {roles.map(r => (
              <div key={r.key} className="flex items-center gap-3">
                <span className="flex-1 text-sm font-semibold text-slate-700">{r.label}</span>
                <Input type="number" min="0" step="0.01" value={vals[r.key] ?? ''} onChange={e => setVals(v => ({ ...v, [r.key]: e.target.value }))}
                  placeholder="Not set" className="w-32 h-9 text-right" data-testid={`template-rate-${r.key}`} />
              </div>
            ))}
          </div>
          <Field label={`Budget per league (${currency}, optional)`}>
            <Input type="number" min="0" step="0.01" value={budget} onChange={e => setBudget(e.target.value)} placeholder="No budget" data-testid="template-budget" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !name.trim() || !filled} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="template-save">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{editing ? 'Save changes' : 'Save template'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
